import { QueryClientProvider } from "@tanstack/react-query";
import { createRootRoute, HeadContent, Outlet } from "@tanstack/react-router";
import { lazy, type ReactNode, Suspense } from "react";
import { AppErrorBoundary } from "@/app/AppErrorBoundary";
import { LegacyDraftStorageCleanup } from "@/app/LegacyDraftStorageCleanup";
import { DesktopLayout } from "@/app/layouts/DesktopLayout";
import { MainContent } from "@/app/layouts/layoutAccessibility";
import { MobileLayout } from "@/app/layouts/MobileLayout";
import { SideMenuProvider } from "@/app/layouts/SideMenuProvider";
import { useRootPresenter } from "@/app/useRootPresenter";
import { AnnouncementStatusProvider } from "@/features/announce/components/AnnouncementStatusProvider";
import { CatalogLayoutInitializer } from "@/features/catalog/components/CatalogLayoutInitializer";
import { DockingOnboardingGate } from "@/features/mix-onboarding/components/DockingOnboardingGate";
import { R18BlurOnboardingGate } from "@/features/r18-blur-onboarding/components/R18BlurOnboardingGate";
import { cn } from "@/shared/lib/cn";
import { ModalProvider } from "@/shared/ui/overlay";
import { ToastProvider } from "@/shared/ui/toast";

// MiniPlayer は再生開始時に初めてロード（motion/react や Player 周辺コードを初期 bundle に含めない）
const LazyMiniPlayer: React.LazyExoticComponent<React.ComponentType> = lazy(
  () =>
    import("@/features/player/ui/MiniPlayer").then((m) => ({
      default: m.MiniPlayer,
    })),
);
const LazyJukeboxPlaybackSession: React.LazyExoticComponent<
  React.ComponentType<{ routeActive: boolean }>
> = lazy(() =>
  import("@/features/jukebox/JukeboxPlaybackSession").then((module) => ({
    default: module.JukeboxPlaybackSession,
  })),
);

interface RootLayoutProps {
  children: ReactNode;
  isStandaloneJukebox: boolean;
}

/** standaloneなジュークボックスには告知UIとそのmeta pollをマウントしない。 */
export const RootLayout: React.FunctionComponent<RootLayoutProps> = ({
  children,
  isStandaloneJukebox,
}: RootLayoutProps) => {
  if (isStandaloneJukebox) return <>{children}</>;

  return (
    <AnnouncementStatusProvider>
      <SideMenuProvider>{children}</SideMenuProvider>
    </AnnouncementStatusProvider>
  );
};

const RootComponent: React.FunctionComponent = () => {
  const presentation = useRootPresenter();
  return <RootView {...presentation} />;
};

function RootView({
  queryClient,
  privacyMode,
  miniPlayerVisible,
  jukeboxPlaybackSessionActive,
  isDesktop,
  hasFinePointer,
  pathname,
  isStandaloneJukebox,
  deferCatalogLayout,
}: ReturnType<typeof useRootPresenter>): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <LegacyDraftStorageCleanup />
      <HeadContent />
      <div
        id="app-root"
        className={cn(
          "min-h-screen bg-background text-foreground",
          privacyMode && "privacy-vignette",
        )}
      >
        <ModalProvider fixedBodyScrollLock={!hasFinePointer}>
          <AppErrorBoundary>
            {/* 初回パネル中はカタログ側をマウントさせない(取得とサムネイルを止める) */}
            <DockingOnboardingGate isDesktop={isDesktop} pathname={pathname}>
              {/* /mixの初回パネルはchildrenごと差し替えるため、この位置なら
                  ぼかし案内モーダルはパネル完了後に初表示され、重ならない */}
              {!isStandaloneJukebox && <R18BlurOnboardingGate />}
              <CatalogLayoutInitializer
                deferChildren={deferCatalogLayout}
                isDesktop={isDesktop}
                pathname={pathname}
              >
                <RootLayout isStandaloneJukebox={isStandaloneJukebox}>
                  {isStandaloneJukebox ? (
                    <MainContent className="min-h-screen">
                      <Outlet />
                    </MainContent>
                  ) : isDesktop ? (
                    <DesktopLayout>
                      <Outlet />
                    </DesktopLayout>
                  ) : (
                    <MobileLayout>
                      <Outlet />
                    </MobileLayout>
                  )}
                </RootLayout>
              </CatalogLayoutInitializer>
            </DockingOnboardingGate>
            {miniPlayerVisible && (
              <Suspense fallback={null}>
                <LazyMiniPlayer />
              </Suspense>
            )}
            {(pathname === "/jukebox" || jukeboxPlaybackSessionActive) && (
              <Suspense fallback={null}>
                <LazyJukeboxPlaybackSession
                  routeActive={pathname === "/jukebox"}
                />
              </Suspense>
            )}
          </AppErrorBoundary>
        </ModalProvider>
        <ToastProvider />
      </div>
    </QueryClientProvider>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
});
